import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { councilPortal } from '../services/api';

function StatCard({ label, value, sub, color = 'blue' }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-700 border-blue-100',
    red:    'bg-red-50 text-red-700 border-red-100',
    green:  'bg-green-50 text-green-700 border-green-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <p className="text-sm font-medium opacity-75">{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

export default function CouncilDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    councilPortal.dashboard()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  if (!data) return <p className="text-gray-500">Failed to load dashboard.</p>;

  const b = data.building;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{b?.name}</h1>
        <p className="text-gray-500 text-sm mt-1">{b?.address} — Council Overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Units" value={data.total_units} color="blue" />
        <StatCard label="Open Work Orders" value={data.open_workorders} color={data.open_workorders > 5 ? 'red' : 'yellow'} />
        <StatCard label="Units in Arrears" value={data.arrears_units} color={data.arrears_units > 0 ? 'red' : 'green'} />
        <StatCard label="YTD Spend" value={`$${Number(data.ytd_spend).toLocaleString()}`} color="blue" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Next meeting */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Next Meeting</h2>
          {data.next_meeting ? (
            <div className="space-y-2">
              <p className="font-medium text-gray-900">{data.next_meeting.title}</p>
              <p className="text-sm text-gray-500">
                {new Date(data.next_meeting.date).toLocaleDateString('en-CA', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </p>
              {data.next_meeting.location && (
                <p className="text-sm text-gray-500">{data.next_meeting.location}</p>
              )}
              <Link to="/council/meetings"
                className="inline-block mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                View all meetings →
              </Link>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No upcoming meetings scheduled.</p>
          )}
        </div>

        {/* Recent work orders */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Recently Completed</h2>
          {data.recent_workorders?.length > 0 ? (
            <ul className="space-y-2">
              {data.recent_workorders.map((w) => (
                <li key={w.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 truncate">{w.title}</span>
                  <span className="text-gray-400 ml-2 shrink-0">
                    {w.completed_date ? new Date(w.completed_date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-400 text-sm">No recently completed work orders.</p>
          )}
          <Link to="/council/workorders"
            className="inline-block mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium">
            View all work orders →
          </Link>
        </div>
      </div>
    </div>
  );
}
